import { useMemo, useReducer } from 'react'
import type {
  CouponState,
  TypeAction,
  UsageLimitByUserType,
  UsageLimitType,
} from '../../pages/Coupon/types'
import { ActionTypes } from '../../pages/Coupon/enums'
import { getDefaultEndDate, getDefaultStartDate } from '@/resources/utils'
import { DateRange } from '@/components/generics/Calendar/types'

const initialState: CouponState = {
  id: '',
  couponName: '',
  couponStatus: true,
  typeOfCoupon: 'PERCENTAGE',
  couponValue: '',
  minimumPurchase: '',
  maximumDiscount: '',
  validateDate: [getDefaultStartDate(), getDefaultEndDate()],
  usageLimit: '',
  usageLimitByUser: '',
  usersRestriction: [],
  usageLimitType: 'limited',
  usageLimitByUserType: 'limited',
}

function reducer(state: CouponState, { type, payload }: TypeAction) {
  switch (type) {
    case ActionTypes.SET_COUPON_NAME:
      return { ...state, couponName: payload }
    case ActionTypes.SET_COUPON_STATUS:
      return { ...state, couponStatus: payload }
    case ActionTypes.SET_TYPE_OF_COUPON:
      return { ...state, typeOfCoupon: payload }
    case ActionTypes.SET_COUPON_VALUE:
      return { ...state, couponValue: payload }
    case ActionTypes.SET_MINIMUM_PURCHASE:
      return { ...state, minimumPurchase: payload }
    case ActionTypes.SET_MAXIMUM_DISCOUNT:
      return { ...state, maximumDiscount: payload }
    case ActionTypes.SET_VALIDATE_DATE:
      return { ...state, validateDate: payload }
    case ActionTypes.SET_USAGE_LIMIT:
      return { ...state, usageLimit: payload }
    case ActionTypes.SET_USAGE_LIMIT_BY_USER:
      return { ...state, usageLimitByUser: payload }
    case ActionTypes.SET_USERS_RESTRICTION:
      return { ...state, usersRestriction: payload }
    case ActionTypes.SET_COUPON_TO_EDIT:
      return { ...state, ...payload }
    case ActionTypes.SET_USAGE_LIMIT_TYPE:
      return { ...state, usageLimitType: payload }
    case ActionTypes.SET_USAGE_LIMIT_BY_USER_TYPE:
      return { ...state, usageLimitByUserType: payload }
  }
}

const useCouponStore = () => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const actions = useMemo(
    () => ({
      setCouponName: (couponName: string) =>
        dispatch({ type: ActionTypes.SET_COUPON_NAME, payload: couponName }),
      setCouponStatus: (couponStatus: boolean) =>
        dispatch({
          type: ActionTypes.SET_COUPON_STATUS,
          payload: couponStatus,
        }),
      setTypeOfCoupon: (typeOfCoupon: 'PERCENTAGE' | 'FIXED_AMOUNT') =>
        dispatch({
          type: ActionTypes.SET_TYPE_OF_COUPON,
          payload: typeOfCoupon,
        }),
      setCouponValue: (couponValue: string) =>
        dispatch({
          type: ActionTypes.SET_COUPON_VALUE,
          payload: couponValue,
        }),
      setMinimumPurchase: (minimumPurchase: string) =>
        dispatch({
          type: ActionTypes.SET_MINIMUM_PURCHASE,
          payload: minimumPurchase,
        }),
      setMaximumDiscount: (maximumDiscount: string) =>
        dispatch({
          type: ActionTypes.SET_MAXIMUM_DISCOUNT,
          payload: maximumDiscount,
        }),
      setValidateDate: (validateDate: DateRange) =>
        dispatch({
          type: ActionTypes.SET_VALIDATE_DATE,
          payload: validateDate,
        }),
      setUsageLimit: (usageLimit: string) =>
        dispatch({ type: ActionTypes.SET_USAGE_LIMIT, payload: usageLimit }),
      setUsageLimitByUser: (usageLimitByUser: string) =>
        dispatch({
          type: ActionTypes.SET_USAGE_LIMIT_BY_USER,
          payload: usageLimitByUser,
        }),
      setUsersRestriction: (usersRestriction: string[]) =>
        dispatch({
          type: ActionTypes.SET_USERS_RESTRICTION,
          payload: usersRestriction,
        }),
      setCouponToEdit: (payload: CouponState) =>
        dispatch({
          type: ActionTypes.SET_COUPON_TO_EDIT,
          payload: payload,
        }),
      setUsageLimitType: (payload: UsageLimitType) =>
        dispatch({
          type: ActionTypes.SET_USAGE_LIMIT_TYPE,
          payload: payload,
        }),
      setUsageLimitByUserType: (payload: UsageLimitByUserType) =>
        dispatch({
          type: ActionTypes.SET_USAGE_LIMIT_BY_USER_TYPE,
          payload: payload,
        }),
    }),
    [dispatch]
  )

  return {
    ...state,
    actions,
  }
}

export default useCouponStore
